import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import {
  AccountCircleOutlined,
  Close,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  HelpOutline,
  Login,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from '@mui/material';

import UserProfileModal from '@components/UserProfileModal/UserProfileModal';
import { ICategory } from '@interfaces/resources';
import { INav } from '@interfaces/routes';
import { headerAdditionalNav, headerNav, headerProfileNav } from '@routes/header';
import urls from '@routes/urls';
import { authTokenSelector } from '@store/features/auth/token/selectors';
import { userDataSelector } from '@store/features/auth/user/selectors';
import {
  canAccessAdminSelector,
  canAccessMedicationLookupSelector,
  canAccessSupplementInquirySelector,
  dashboardOrgDataSelector,
} from '@store/features/dashboardOrgInfo/selectors';
import { fetchCategories, selectCategories } from '@store/features/recources/slice';
import useDispatch from '@store/hooks/useDispatch';
import getShortenEmail from '@utils/getShortenEmail';
import handleLogout from '@utils/handleLogout';

import { IHeaderDrawerProps } from './HeaderDrawer.interface';

import './style.scss';

const HeaderDrawer: FC<IHeaderDrawerProps> = ({ open, onClose }) => {
  const authToken = useSelector(authTokenSelector);
  const user = useSelector(userDataSelector);
  const canAccessSupplementInquiry = useSelector(canAccessSupplementInquirySelector);
  const canAccessMedicationLookup = useSelector(canAccessMedicationLookupSelector);
  const canAccessAdmin = useSelector(canAccessAdminSelector);

  const { t } = useTranslation();

  const [isAboutOpen, setIsAboutOpen] = useState<boolean>(false);
  const [isResourceOpen, setResourceOpen] = useState<boolean>(false);
  const [isAccountOpen, setIsAccountOpen] = useState<boolean>(false);

  const [openModal, setOpen] = useState<boolean>(false);
  const categories = useSelector(selectCategories);

  const dashboardOrgInfo = useSelector(dashboardOrgDataSelector);
  const infoPageTitle = dashboardOrgInfo?.infoPageTitle;

  const dispatch = useDispatch();
  useEffect(() => {
    if (user && !user.isAdmin && user.organizationId !== null) {
      dispatch(fetchCategories());
    }
  }, [dispatch, user]);

  return (
    <>
      <UserProfileModal open={openModal} onClose={(): void => setOpen(false)} />

      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        className="header-drawer"
        sx={{ display: { lg: 'none' } }}
        hideBackdrop
      >
        <Box role="presentation" className="header-drawer__wrapper">
          <List
            component="nav"
            aria-labelledby="sidebar-navigation"
            className="header-drawer__list"
            subheader={
              <ListSubheader
                component="div"
                id="header-drawer__subheader"
                className="header-drawer__subheader"
              >
                <span>{user?.email ? getShortenEmail(user.email, 19) : ''}</span>
                <IconButton onClick={onClose}>
                  <Close color="primary" />
                </IconButton>
              </ListSubheader>
            }
          >
            {headerNav.map((nav: INav) => {
              if (!canAccessMedicationLookup && nav.path === urls.medications) {
                return null;
              }
              if (!canAccessSupplementInquiry && nav.path === urls.inquiryDraft) {
                return null;
              }

              return (
                <NavLink
                  className="header-drawer__list-item-link"
                  onClick={onClose}
                  key={nav.path}
                  to={nav.path}
                >
                  <ListItemButton className="header-drawer__list-item-button">
                    <ListItemIcon className="header-drawer__list-item-icon">
                      {nav.icon}
                    </ListItemIcon>
                    <ListItemText
                      className="header-drawer__list-item-text"
                      primary={t(nav.i18nTitle)}
                    />
                  </ListItemButton>
                </NavLink>
              );
            })}

            {infoPageTitle && (
              <NavLink className="header-drawer__list-item-link" to={urls.infoPage}>
                <ListItemButton className="header-drawer__list-item-button">
                  <ListItemIcon className="header-drawer__list-item-icon">
                    <InfoOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText className="header-drawer__list-item-text" primary={infoPageTitle} />
                </ListItemButton>
              </NavLink>
            )}

            {canAccessAdmin && (
              <NavLink
                className="header-drawer__list-item-link"
                onClick={onClose}
                to={urls.adminInquiries}
              >
                <ListItemButton className="header-drawer__list-item-button">
                  <ListItemIcon className="header-drawer__list-item-icon">
                    <Box component="img" src="/images/img-admin.svg" />
                  </ListItemIcon>
                  <ListItemText
                    className="header-drawer__list-item-text"
                    primary={t('headerNav.adminInquiries')}
                  />
                </ListItemButton>
              </NavLink>
            )}

            {user && !user.isAdmin && (
              <ListItemButton
                onClick={() => setResourceOpen(!isResourceOpen)}
                className="header-drawer__list-item-button"
              >
                <ListItemIcon className="header-drawer__list-item-icon">
                  <Box component="img" src="/images/img-resources.svg" />
                </ListItemIcon>
                <ListItemText
                  className="header-drawer__list-item-text"
                  primary={t('headerNav.resources')}
                />
                {isResourceOpen ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
              </ListItemButton>
            )}

            {user && !user.isAdmin && categories && (
              <Collapse in={isResourceOpen} timeout="auto" unmountOnExit>
                <List component="div" className="header-drawer__list">
                  {categories.map(({ id, name }: ICategory) => (
                    <a
                      aria-label={`${id}`}
                      className="header-drawer__list-item-link"
                      onClick={onClose}
                      key={id}
                      href={`/category/${id}`}
                      rel="noreferrer"
                    >
                      <ListItemButton
                        sx={{ pl: 4 }}
                        className="header-drawer__list-item-button"
                        disableGutters
                      >
                        <ListItemIcon className="header-drawer__list-item-icon" />
                        <ListItemText className="header-drawer__list-item-text" primary={name} />
                      </ListItemButton>
                    </a>
                  ))}
                </List>
              </Collapse>
            )}

            {canAccessAdmin && (
              <NavLink onClick={onClose} to={urls.adminHome}>
                <ListItemButton className="header-drawer__list-item-button">
                  <ListItemIcon className="header-drawer__list-item-icon">
                    <Box component="img" src="/images/img-admin.svg" />
                  </ListItemIcon>
                  <ListItemText
                    className="header-drawer__list-item-text"
                    primary={t('headerNav.admin')}
                  />
                </ListItemButton>
              </NavLink>
            )}

            <ListItemButton
              onClick={() => setIsAboutOpen(!isAboutOpen)}
              className="header-drawer__list-item-button"
            >
              <ListItemIcon className="header-drawer__list-item-icon">
                <HelpOutline />
              </ListItemIcon>
              <ListItemText
                className="header-drawer__list-item-text"
                primary={t('headerNav.about')}
              />
              {isAboutOpen ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
            </ListItemButton>

            <Collapse in={isAboutOpen} timeout="auto" unmountOnExit>
              <List component="div" className="header-drawer__list">
                {headerAdditionalNav.map((nav: INav) => (
                  // eslint-disable-next-line react/jsx-no-target-blank
                  <a
                    aria-label={nav.path}
                    className="header-drawer__list-item-link"
                    onClick={onClose}
                    key={nav.path}
                    href={nav.path}
                    target="_blank"
                  >
                    <ListItemButton
                      sx={{ pl: 4 }}
                      className="header-drawer__list-item-button"
                      disableGutters
                    >
                      <ListItemIcon className="header-drawer__list-item-icon" />
                      <ListItemText
                        className="header-drawer__list-item-text"
                        primary={t(nav.i18nTitle)}
                      />
                    </ListItemButton>
                  </a>
                ))}
              </List>
            </Collapse>

            <ListItemButton
              onClick={() => setIsAccountOpen(!isAccountOpen)}
              className="header-drawer__list-item-button"
            >
              <ListItemIcon className="header-drawer__list-item-icon">
                <AccountCircleOutlined />
              </ListItemIcon>
              <ListItemText
                className="header-drawer__list-item-text"
                primary={t('headerProfileNav.account')}
              />
              {isAccountOpen ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
            </ListItemButton>

            <Collapse in={isAccountOpen} timeout="auto" unmountOnExit>
              <List component="div" className="header-drawer__list">
                {headerProfileNav.map((nav: INav) => {
                  if (!canAccessSupplementInquiry && nav.path === urls.inquiries) {
                    return null;
                  }

                  return (
                    <NavLink
                      className="header-drawer__list-item-link"
                      onClick={onClose}
                      key={nav.path}
                      to={nav.path}
                    >
                      <ListItemButton
                        sx={{ pl: 4 }}
                        className="header-drawer__list-item-button"
                        disableGutters
                      >
                        <ListItemIcon className="header-drawer__list-item-icon" />
                        <ListItemText
                          className="header-drawer__list-item-text"
                          primary={t(nav.i18nTitle)}
                        />
                      </ListItemButton>
                    </NavLink>
                  );
                })}

                {user && (
                  <ListItemButton onClick={(): void => setOpen(true)}>
                    <ListItemIcon className="header-drawer__list-item-icon" />
                    <ListItemText
                      className="header-drawer__list-item-text"
                      primary={t('headerProfileNav.profile')}
                    />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </List>

          <List component="nav" aria-labelledby="sidebar-auth" className="header-drawer__auth">
            {authToken ? (
              <ListItemButton onClick={handleLogout} className="header-drawer__list-item-button">
                <ListItemIcon className="header-drawer__list-item-icon">
                  <ExitToApp />
                </ListItemIcon>
                <ListItemText
                  className="header-drawer__list-item-text"
                  primary={t('common.auth.logout')}
                />
              </ListItemButton>
            ) : (
              <NavLink className="header-drawer__list-item-link" to={urls.login}>
                <ListItemButton className="header-drawer__list-item-button">
                  <ListItemIcon className="header-drawer__list-item-icon">
                    <Login />
                  </ListItemIcon>
                  <ListItemText
                    className="header-drawer__list-item-text"
                    primary={t('common.auth.login')}
                  />
                </ListItemButton>
              </NavLink>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default HeaderDrawer;
